import { Crown, Globe, Bot, Users } from 'lucide-react'
import { CARD_STYLE, COLORS, INPUT_CLASS, INPUT_STYLE } from '../shared'

function slugify(name) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function BrowserMock({ platformName }) {
  const slug = slugify(platformName || '')
  const displayUrl = slug
    ? `verboo.ai/dashboard/${slug}`
    : 'verboo.ai/dashboard/...'

  return (
    <div
      className="mx-auto w-full max-w-lg overflow-hidden rounded-xl"
      style={CARD_STYLE}
    >
      {/* Title bar */}
      <div
        className="flex items-center gap-2 border-b px-4 py-2.5"
        style={{
          borderColor: 'rgba(255,255,255,0.06)',
          background: 'rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FE3456]/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FE9F34]/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#34FEAD]/60" />
        </div>
        <div
          className="ml-2 flex h-6 flex-1 items-center gap-1.5 rounded-md px-3 font-mono text-[10px]"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.45)',
          }}
        >
          <span style={{ color: 'rgba(52, 254, 173, 0.7)' }}>●</span>
          <span className="truncate">{displayUrl}</span>
        </div>
      </div>

      {/* Dashboard preview */}
      <div className="flex min-h-[180px]">
        {/* Sidebar */}
        <div
          className="flex w-[140px] shrink-0 flex-col gap-3 border-r p-4"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          {/* Platform name in sidebar */}
          <div className="flex items-center gap-2">
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
              style={{
                background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
              }}
            >
              <span className="text-[9px] font-bold text-white">
                {(platformName || 'P')[0].toUpperCase()}
              </span>
            </div>
            <span
              className="truncate text-[10px] font-semibold"
              style={{ color: COLORS.text }}
            >
              {platformName || 'Plataforma'}
            </span>
          </div>

          {/* Nav items */}
          <div className="mt-2 flex flex-col gap-1.5">
            <NavItem icon={Bot} label="Agentes" active />
            <NavItem icon={Users} label="Equipe" />
            <NavItem icon={Globe} label="Integrações" />
          </div>
        </div>

        {/* Main content area */}
        <div className="flex flex-1 flex-col gap-3 p-4">
          <div className="flex items-center justify-between">
            <div
              className="h-2 w-16 rounded-full"
              style={{ background: 'rgba(173,52,254,0.4)' }}
            />
            <div
              className="h-2 w-8 rounded-full"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            />
          </div>

          {/* Agent card placeholders */}
          <div className="grid grid-cols-2 gap-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="flex flex-col gap-1.5 rounded-lg p-3"
                style={{
                  background: 'rgba(173,52,254,0.06)',
                  border: '1px solid rgba(173,52,254,0.15)',
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="h-4 w-4 rounded-md"
                    style={{
                      background:
                        i === 1
                          ? 'linear-gradient(135deg, #AD34FE, #7b1fd4)'
                          : 'linear-gradient(135deg, #34ADFE, #1d7fc7)',
                    }}
                  />
                  <div
                    className="h-1.5 w-12 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.15)' }}
                  />
                </div>
                <div
                  className="h-1 w-full rounded-full"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
                <div
                  className="h-1 w-3/4 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-1">
            <span
              className="text-[8px] uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.2)' }}
            >
              Powered by Verboo
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function NavItem({ icon: Icon, label, active }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md px-2 py-1.5"
      style={{
        background: active ? 'rgba(173,52,254,0.12)' : 'transparent',
        color: active ? '#AD34FE' : 'rgba(255,255,255,0.35)',
      }}
    >
      <Icon className="h-3 w-3" />
      <span className="text-[10px] font-medium">{label}</span>
    </div>
  )
}

export default function CreatePlatform({ user, platformName, onNameChange }) {
  const firstName = user?.name?.split(' ')[0] || ''

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 sm:gap-8">
      {/* Heading */}
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest sm:text-[11px]"
          style={{
            background: 'rgba(254, 159, 52, 0.12)',
            border: '1px solid rgba(254, 159, 52, 0.3)',
            color: '#FE9F34',
          }}
        >
          <Crown className="h-3 w-3" />
          {firstName ? `${firstName}, ` : ''}novo cliente
        </span>
        <h2
          className="text-2xl font-bold tracking-tight sm:text-4xl"
          style={{ color: COLORS.text, lineHeight: 1.1 }}
        >
          A configuração do seu cliente{' '}
          <br className="hidden sm:block" />
          começa{' '}
          <span
            style={{
              background: 'linear-gradient(90deg, #FE9F34, #AD34FE)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            aqui
          </span>
        </h2>
        <p
          className="max-w-xl text-sm leading-relaxed sm:text-base"
          style={{ color: COLORS.textMuted }}
        >
          Comece pelo cliente. Em seguida, você cria o primeiro agente e
          conecta o canal que ele vai usar no atendimento.
        </p>
      </div>

      {/* Browser mock */}
      <BrowserMock platformName={platformName} />

      {/* Form card */}
      <div
        className="flex flex-col gap-4 rounded-2xl p-5 sm:rounded-3xl sm:p-8"
        style={CARD_STYLE}
      >
        <div className="flex flex-col gap-2">
          <label
            className="text-[10px] font-semibold uppercase tracking-wider sm:text-[11px]"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            Nome do cliente
          </label>
          <input
            type="text"
            value={platformName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Ex: Clínica Aurora"
            className={INPUT_CLASS}
            style={INPUT_STYLE}
            autoFocus
          />
          <p
            className="text-[11px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            Use o nome pelo qual sua equipe reconhece este cliente.
          </p>
        </div>
      </div>
    </div>
  )
}
